import React, { useEffect, useRef } from 'react';
import RequestBuilder from './RequestBuilder';
import CollectionEditor from './CollectionEditor';
import FolderEditor from './collectionPage/FolderEditor';
import SchemaEditor from './SchemaEditor';
import DbManagerTabView from './dbOverview/DbManagerTabView';
import TaskBoardTab from './TaskBoardTab';
import DocumentEditorTab from './DocumentEditorTab';
import ProjectsTab from './projects/ProjectsTab';
import ApibooLoader from './ui/ApibooLoader';
import {
  useTabsStore,
  selectCurrentRequestTab,
  selectCurrentCollectionTab,
  selectCurrentSchemaTab,
  selectCurrentDocumentTab,
} from '../store/tabsStore';
import { useVariablesStore } from '../store/variablesStore';
import { useRequestFolderVariables } from '../hooks/useRequestFolderVariables';
import { isFolderTab } from '../utils/collectionTabState';
import { syncFolderTabTitles } from '../utils/folderTabs';

const emptyState = (label) => (
  <div className="editor-empty-surface flex h-full min-h-[120px] items-center justify-center px-4 text-sm text-gray-500">
    {label}
  </div>
);

/**
 * The active editor for the current tab (collection / request / schema), with
 * the read-only banner and empty/loading states. Tab + variable state are read
 * from their stores; behavior handlers, register-callbacks and the collection
 * context come from App as props.
 */
export default function EditorPane({
  collections,
  schemas,
  currentWorkspace,
  workspaceReadOnly,
  currentCollectionVariables,
  requestCollectionName,
  requestCollectionAuth,
  requestFolderAuthChain,
  selectedRequest,
  t,
  // collection editor
  handleCollectionTabDataChange,
  saveCollectionFromTab,
  handleVariableUpdate,
  // request editor
  clearTabLoading,
  updateCurrentRequestTabStable,
  updateCurrentRequestTabResponse,
  saveRequest,
  addToHistory,
  history,
  selectFromHistory,
  clearHistoryForRequest,
  registerRequestBuilderLiveState,
  registerRequestBuilderSendHandler,
  handleCommitRequestName,
  requestBuilderRenameRef,
  // schema editor
  updateCurrentSchemaTabStable,
  saveSchemaTab,
  openSchemaTab,
  handleCommitSchemaName,
  schemaEditorRenameRef,
}) {
  const activeTabType = useTabsStore((s) => s.activeTabType);
  const tabsHydrating = useTabsStore((s) => s.tabsHydrating);
  const requestTabSyncRevision = useTabsStore((s) => s.requestTabSyncRevision);
  const urlFocusNonce = useTabsStore((s) => s.urlFocusNonce);
  const currentRequestTab = useTabsStore(selectCurrentRequestTab);
  const currentCollectionTab = useTabsStore(selectCurrentCollectionTab);
  const currentSchemaTab = useTabsStore(selectCurrentSchemaTab);
  const currentDocumentTab = useTabsStore(selectCurrentDocumentTab);
  const activeProjectsTabId = useTabsStore((s) => s.activeProjectsTabId);

  const selectedEnvironment = useVariablesStore((s) => s.selectedEnvironment);
  const workspaceVariables = useVariablesStore((s) => s.workspaceVariables);
  const globalVariables = useVariablesStore((s) => s.globalVariables);
  // The active request's folder chain variables (hooks/useRequestFolderVariables).
  const requestFolderVariables = useRequestFolderVariables();

  // Flips true the first time a task board tab is active and never resets, so
  // TaskBoardTab is rendered (hidden) from then on rather than remounted.
  const taskBoardEverOpenedRef = useRef(false);

  // A folder page's tab title follows the folder's name (owner D1).
  useEffect(() => { syncFolderTabTitles(collections); }, [collections]);

  // Task Manager stays mounted once first opened, then is hidden (not
  // unmounted) on tab switches. Unmounting would drop the loaded board + its
  // in-memory cache and re-run mount effects — causing the visible flash/wiggle
  // and re-focusing empty tasks every time you return to a board tab.
  const isTaskBoard = activeTabType === 'task-manager-board';
  if (isTaskBoard) taskBoardEverOpenedRef.current = true;

  const renderActiveContent = () => {
  if (activeTabType === 'collection' && isFolderTab(currentCollectionTab)) {
    // A folder page (owner D1): same shell, its own save and edit gate.
    return (
      <FolderEditor
        key={currentCollectionTab.id}
        initialData={currentCollectionTab}
        onDataChange={handleCollectionTabDataChange}
        selectedEnvironment={selectedEnvironment}
        workspaceVariables={workspaceVariables}
        globalVariables={globalVariables}
        currentWorkspaceId={currentWorkspace?.id}
        onVariableUpdate={handleVariableUpdate}
      />
    );
  }

  if (activeTabType === 'collection') {
    return currentCollectionTab ? (
      <CollectionEditor
        key={currentCollectionTab.id}
        initialData={currentCollectionTab}
        canEdit={
          collections.find(
            (c) => String(c.id) === String(currentCollectionTab.collectionId)
          )?.can_edit !== false
        }
        onDataChange={handleCollectionTabDataChange}
        onSave={saveCollectionFromTab}
        selectedEnvironment={selectedEnvironment}
        workspaceVariables={workspaceVariables}
        globalVariables={globalVariables}
        currentWorkspaceId={currentWorkspace?.id}
        onVariableUpdate={handleVariableUpdate}
      />
    ) : (
      emptyState('No collection tab selected')
    );
  }

  if (activeTabType === 'request') {
    if (tabsHydrating) {
      return (
        <div className="apiboo-panel-loader">
          <ApibooLoader size={110} />
        </div>
      );
    }
    return currentRequestTab ? (
      <>
        {workspaceReadOnly && (
          <div className="flex-shrink-0 border-b border-amber-200 bg-amber-50 px-4 py-2 text-xs text-amber-900 dark:border-amber-900/40 dark:bg-amber-950/40 dark:text-amber-100">
            {t('viewOnly.banner')}
          </div>
        )}
        <RequestBuilder
          key={currentWorkspace?.id ?? 'ws'}
          initialData={currentRequestTab}
          onClearTabLoading={clearTabLoading}
          syncRevision={requestTabSyncRevision}
          collectionName={requestCollectionName}
          collectionAuth={requestCollectionAuth}
          folderAuthChain={requestFolderAuthChain}
          folderVariables={requestFolderVariables}
          onDataChange={updateCurrentRequestTabStable}
          onUiStateChange={updateCurrentRequestTabResponse}
          onSaveRequest={saveRequest}
          onAddToHistory={addToHistory}
          history={history}
          onSelectFromHistory={selectFromHistory}
          onClearRequestHistory={clearHistoryForRequest}
          onUpdateResponse={updateCurrentRequestTabResponse}
          selectedRequest={selectedRequest}
          selectedEnvironment={selectedEnvironment}
          workspaceVariables={workspaceVariables}
          globalVariables={globalVariables}
          collectionVariables={currentCollectionVariables}
          currentWorkspaceId={currentWorkspace?.id}
          isTeamWorkspace={currentWorkspace?.type === 'team'}
          onVariableUpdate={handleVariableUpdate}
          schemas={schemas}
          response={currentRequestTab?.response}
          loading={currentRequestTab?.loading}
          onRegisterRenameShortcut={(fn) => {
            requestBuilderRenameRef.current = fn;
          }}
          onRegisterLiveState={registerRequestBuilderLiveState}
          onRegisterSendHandler={registerRequestBuilderSendHandler}
          onRequestNameCommit={handleCommitRequestName}
          focusUrlNonce={urlFocusNonce}
          readOnly={workspaceReadOnly}
        />
      </>
    ) : (
      emptyState('No request tab selected')
    );
  }

  if (activeTabType === 'schema') {
    return currentSchemaTab ? (
      <SchemaEditor
        key={currentSchemaTab.id}
        initialData={currentSchemaTab}
        schemas={schemas}
        onDataChange={updateCurrentSchemaTabStable}
        onSave={saveSchemaTab}
        onOpenSchemaRef={openSchemaTab}
        onRegisterRenameShortcut={(fn) => {
          schemaEditorRenameRef.current = fn;
        }}
        onSchemaNameCommit={handleCommitSchemaName}
        selectedEnvironment={selectedEnvironment}
        workspaceVariables={workspaceVariables}
        globalVariables={globalVariables}
        currentWorkspaceId={currentWorkspace?.id}
        onVariableUpdate={handleVariableUpdate}
      />
    ) : (
      emptyState('No schema tab selected')
    );
  }

  if (activeTabType === 'db-manager-table') {
    return <DbManagerTabView />;
  }

  if (activeTabType === 'projects-project') {
    // Keyed by tab: an error or a half-typed member search must not follow the
    // user from one project's tab into another's.
    return <ProjectsTab key={activeProjectsTabId || 'projects'} />;
  }

  if (activeTabType === 'document-editor') {
    return currentDocumentTab ? (
      <DocumentEditorTab key={currentDocumentTab.id} documentId={currentDocumentTab.documentId} />
    ) : (
      emptyState('Select a document from the sidebar, or create a new one.')
    );
  }

    return null;
  };

  return (
    <>
      {!isTaskBoard && renderActiveContent()}
      {taskBoardEverOpenedRef.current && (
        <div className="h-full min-h-0" hidden={!isTaskBoard}>
          <TaskBoardTab isActive={isTaskBoard} />
        </div>
      )}
    </>
  );
}

